import { ApiMessage, ApiResponseHandler } from '../ApiResponseHandler';
import ThomApiClient from '../ThomApiClient';
import { ProgressNoteType } from '../../utils/type';
import dayjs from 'dayjs';
import { CreateDeleteProgressNoteType } from '../../pages/ProgressNote/ProgressNote';
import { THOM_BASE_URL } from '../Constant';
import { getCurrentWeekSundayAsDate } from '../../utils/utilities';

export interface ProgressNoteServiceCodeVisitType {
  serviceCodeLabel: string | null;
  maxHoursPerVisit: number | null;
  isMaxHoursPerVisitBreached: boolean | null;
  maxHoursPerDay: number | null;
  isMaxHoursPerDayBreached: boolean | null;
  maxHoursPerWeek: number | null;
  isMaxHoursPerWeekBreached: boolean | null;
  maxVisitsPerDiscipline: number | null;
  isMaxVisitsPerDisciplineBreached: boolean | null;
  maxVisitsPerDay: number | null;
  isMaxVisitsPerDayBreached: boolean | null;
  maxVisitsPerWeek: number | null;
  isMaxVisitsPerWeekBreached: boolean | null;
  maxVisitsSixMonths: number | null;
  isMaxVisitsSixMonthsBreached: boolean | null;
  maxVisitsLifetime: number | null;
  isMaxVisitsLifetimeBreached: boolean | null;
  maxVisitsPreBirth: number | null;
  isMaxVisitsPreBirthBreached: boolean | null;
  maxVisitsUnderTwoMonths: number | null;
  isMaxVisitsUnderTwoMonthsBreached: boolean | null;
  maxVisitsOverTwoMonths: number | null;
  isMaxVisitsOverTwoMonthsBreached: boolean | null;
}

export interface ProgressNotesListResponseType {
  progressNotesId: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  suffix: string | null;
  enrolmentId: string | null;
  appointmentType: string | null;
  appointmentStartTime: string | null;
  attendees: AttendeeDetailsType[] | null;
  durationDec: number | null;
  status: string | null;
  commonAppointmentId: string | null;
  appointmentId: string | null;
  attendeeId: string | null;
  clientId: string | null;
}

export interface UpdateProgressNoteResponse extends ApiMessage {
  showPopup: boolean | null;
  visits: ProgressNoteServiceCodeVisitType[];
}

export type AttendeeDetailsType = {
  attendeeId: string | null;
  firstName: string | null;
  middleName: string | null;
  lastName: string | null;
  suffix: string | null;
};

export interface ProgressNotesListResposense {
  progressNotes: ProgressNotesListResponseType[];
  count: number;
}
export const getProgressNote = async (
  attendeeId: string,
  commonAppointmentId: string,
  referralProgramId: string,
): Promise<ProgressNoteType> => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get(
      `progress-notes/v3?attendeeId=${attendeeId}&commonAppointmentId=${commonAppointmentId}&timezone=${timezone}&referralProgramId=${
        referralProgramId ?? ''
      }`,
    ),
  );
};

export interface GroupBilling {
  appointmentId: string | null; // mandatory
  attendeeId: string | null; // mandatory
  groupBillingDurationDec: number | null;
  groupBillingStartTime: string | null;
  billingModified?: boolean | null;
  payrollModified?: boolean | null;
}

export const EditProgressNote = async (
  params: ProgressNoteType,
  attendeeId: string,
  commonAppointmentId: string,
  byPassServiceCode: boolean,
  referralProgramId: string,
): Promise<UpdateProgressNoteResponse> => {
  const timezone = encodeURIComponent(
    dayjs(getCurrentWeekSundayAsDate()).format('Z'),
  );
  return ApiResponseHandler(
    await ThomApiClient.getInstance().put<
      ProgressNoteType,
      UpdateProgressNoteResponse
    >(
      `progress-notes/v3?attendeeId=${attendeeId}&commonAppointmentId=${commonAppointmentId}&timezone=${timezone}&byPassServiceCode=${byPassServiceCode}&referralProgramId=${
        referralProgramId ?? ''
      }`,
      params,
    ),
  );
};

export const CancelProgressNoteVisit = async (
  body: {
    cancelReason: string;
    status: string;
  },
  attendeeId: string,
  appointmentId: string,
  clientId: string,
  referralProgramId: string,
): Promise<ApiMessage> => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().patch(
      `progress-notes/v3?attendeeId=${attendeeId}&appointmentId=${appointmentId}&clientId=${clientId}&timezone=${timezone}&referralProgramId=${
        referralProgramId ?? ''
      }`,
      body,
    ),
  );
};

export const CreateDeleteProgressNote = async (
  params: CreateDeleteProgressNoteType,
  attendeeId: string,
  appointmentId: string,
  referralProgramId: string,
): Promise<ApiMessage> => {
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().post(
      `progress-notes/v3?attendeeId=${attendeeId}&appointmentId=${appointmentId}&timezone=${timezone}&referralProgramId=${
        referralProgramId ?? ''
      }`,
      params,
    ),
  );
};

export const downloadProgressNote = async (
  clientId: string,
  attendeeId: string,
  commonAppointmentId: string,
  localTimeZone: string,
  referralProgramId: string,
  language = 'en',
): Promise<Blob> => {
  console.log(localTimeZone);
  const timezone = encodeURIComponent(dayjs(new Date()).format('Z'));
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get<Blob>(
      `progress-notes/download?clientId=${clientId}&attendeeId=${attendeeId}&commonAppointmentId=${commonAppointmentId}&timezone=${timezone}&referralProgramId=${
        referralProgramId ?? ''
      }&language=${language}`,
      {},
      {
        baseURL: THOM_BASE_URL,
        responseType: 'blob',
      },
    ),
  );
};

export const UpdateGroupBillig = async (
  params: GroupBilling,
  commonAppointmentId?: string,
): Promise<ApiMessage> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().post(
      `progress-notes/groupBilling?commonAppointmentId=${commonAppointmentId}`,
      params,
    ),
  );
};

export const getProgressNotesListApi = async (
  clientName: string,
  status: string,
  appointmentStartDate: string,
  appointmentEndDate: string,
  attendeeIds: string[],
  page: number,
  size: number,
  sort: string,
  sortType: string,
): Promise<ProgressNotesListResposense> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get(
      `progress-notes/all?clientName=${clientName}&status=${status}&appointmentStartDate=${appointmentStartDate}&attendeeIds=${attendeeIds}&page=${page}&size=${size}&appointmentEndDate=${appointmentEndDate}&sort=${sort}&sortType=${sortType}`,
    ),
  );
};

export const getProgressNotesListOnClientApi = async (
  clientId: string,
  attendeeName: string,
  status: string,
  appointmentStartDate: string,
  appointmentEndDate: string,
  page: number,
  size: number,
  referralProgramId: string,
): Promise<ProgressNotesListResposense> => {
  return ApiResponseHandler(
    await ThomApiClient.getInstance().get(
      `progress-notes/facesheet?clientId=${clientId}&attendeeName=${attendeeName}&status=${status}&appointmentStartDate=${appointmentStartDate}&page=${page}&size=${size}&appointmentEndDate=${appointmentEndDate}&referralProgramId=${
        referralProgramId ?? ''
      }`,
    ),
  );
};
